import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Table, Tag, Tooltip, message, notification } from "antd";
import {
  CloseCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  CreditCardOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import locIcon from "../../../../Assets/Icons/distance.svg";
import regIcon from "../../../../Assets/Icons/id_card.svg";
import taxIcon from "../../../../Assets/Icons/article_person.svg";
import webIcon from "../../../../Assets/Icons/globe_uk.svg";
import docIcon from "../../../../Assets/Icons/document.svg";
import creditIcon from "../../../../Assets/Icons/credit_card_req.svg";
import vehicleIcon from "../../../../Assets/Icons/car.svg";
import "./style.css";
import {
  getApprovalById,
  rejectApproval,
  approveApproval,
  requestRevision,
} from "../../../../API/Admin/Approval&Requests";
import InitialsAvatar from "../../../../Components/InitialsAvatar";
import GenericModal from "../../../../Components/Modals/GenericModal";
import usePageAccess from "../../../../Utlis/usePageAccess";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveFileUrl = (file) => {
  if (!file) return null;
  if (/^https?:\/\//i.test(file) || file.startsWith("blob:")) return file;
  return `${BACKEND_ORIGIN}${file.startsWith("/") ? "" : "/"}${file}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatCurrency = (val) => {
  const num = parseFloat(val);
  return Number.isNaN(num) ? "-" : `$${num.toFixed(2)}`;
};

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar) || avatar.startsWith("blob:")) {
    return avatar;
  }
  return `${BACKEND_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
};

const formatPhone = (phone) => {
  if (!phone || phone === "-") return "-";
  return phone.startsWith("+") ? phone : `+${phone}`;
};

const REQUEST_KINDS = {
  initial: {
    label: "Initial Subscription",
    color: "initial",
    description:
      "First payment after onboarding, for the plan chosen at sign-up.",
  },
  renewal: {
    label: "Subscription Renewal",
    color: "renewal",
    description:
      "Renews the subscription for a new billing cycle. The plan below replaces the current one.",
  },
  addon: {
    label: "Add-on (Mid-cycle)",
    color: "addon",
    description:
      "Adds vehicle slots and/or modules to the running subscription, charged only for the days left in the cycle.",
  },
  standard: {
    label: "Subscription Payment",
    color: "standard",
    description:
      "Payment for the fleet's subscription plan. Review the billing period, modules and vehicle count below before approving.",
  },
};

const getRequestKind = (data) => {
  if (data?.type !== "subscription_payment") return null;
  if (data.is_initial_subscription) return "initial";
  if (data.is_subscription_renewal) return "renewal";
  if (data.is_subscription_addon) return "addon";
  return "standard";
};

const formatCycle = (cycle) =>
  cycle ? `${cycle.charAt(0).toUpperCase()}${cycle.slice(1)}` : "-";

const SubscriptionPaymentRequest = () => {
  const { canEdit } = usePageAccess("approvals-requests");
  const navigate = useNavigate();
  const location = useLocation();
  const passedId = location.state?.id;
  const [remarks, setRemarks] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [requestingRevision, setRequestingRevision] = useState(false);
  const [status, setStatus] = useState("-");
  const [logoFailed, setLogoFailed] = useState(false);
  const [request, setRequest] = useState({
    legalName: "-",
    companyLogo: null,
    registrationNumber: "-",
    contactEmail: "-",
    contactPhone: "-",
    address: "-",
    submittedByName: "-",
    submittedByEmail: "-",
    amount: "-",
    paymentMethod: "-",
    paymentDate: "-",
    proofFile: null,
    isWiPay: false,
    wipayStatus: "-",
    wipayStatusKey: "",
    kind: null,
    billingCycle: null,
    modules: [],
    currentVehicleLimit: null,
    expiresOn: null,
    addonPricing: null,
  });

  const statusMap = {
    approved: "status-approved",
    rejected: "status-denied",
    denied: "status-denied",
    pending: "status-pending",
    revision_requested: "status-revision-requested",
  };
  const currentStatus = status;
  const formatStatus = (value) =>
    value
      ? value
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : value;

  useEffect(() => {
    if (!passedId) {
      message.error(
        "No request selected. Please open this page from the Approvals & Requests list.",
      );
      return;
    }

    const fetchApproval = async () => {
      try {
        const res = await getApprovalById(passedId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          setStatus(data.status || "-");
          setRequest({
            legalName: data.entity?.legal_name || "-",
            companyLogo: resolveAvatarUrl(data.entity?.avatar),
            registrationNumber: data.entity?.registration_number || "-",
            contactEmail: data.entity?.contact_email || "-",
            contactPhone: formatPhone(data.entity?.contact_phone),
            address: data.entity?.address || "-",
            submittedByName: data.submitted_by?.name || "-",
            submittedByAvatar: resolveAvatarUrl(data.submitted_by?.avatar),
            submittedByEmail: data.submitted_by?.email || "-",
            amount: formatCurrency(data.amount),
            requestType: data.type || "",
            vehicleSlots: data.requested_vehicle_slots ?? null,
            paymentMethod: data.payment_method || "-",
            paymentDate: formatDate(data.payment_date),
            proofFile: resolveFileUrl(data.proof_file),
            isWiPay: data.payment_method === "wipay",
            wipayStatus: data.wipay_transaction?.status
              ? data.wipay_transaction.status.charAt(0).toUpperCase() +
                data.wipay_transaction.status.slice(1)
              : "-",
            wipayStatusKey: (
              data.wipay_transaction?.status || ""
            ).toLowerCase(),
            kind: getRequestKind(data),
            billingCycle: data.billing_cycle || null,
            modules: data.requested_modules || [],
            currentVehicleLimit: data.entity?.vehicle_limit ?? null,
            expiresOn: data.entity?.subscription_expires_on || null,
            addonPricing: data.addon_pricing || null,
          });
        } else {
          message.error(res?.data?.message || "Failed to load request.");
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to load request.",
        );
      }
    };

    fetchApproval();
  }, [passedId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleReject = async () => {
    if (!remarks.trim()) {
      notification.warning({
        message: "Audit Notes Required",
        description: "Please enter audit notes before rejecting.",
      });
      return;
    }

    if (!passedId) {
      message.error("No request selected.");
      return;
    }

    setRejecting(true);
    try {
      const res = await rejectApproval(passedId, remarks);

      if (Math.floor(res?.status / 100) === 2) {
        notification.error({
          message: res?.data?.message || "Rejected",
        });
        navigate("/approvals-requests");
      } else {
        message.error(res?.data?.message || "Failed to reject request.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to reject request.",
      );
    } finally {
      setRejecting(false);
    }
  };

  const handleRevision = async () => {
    if (!remarks.trim()) {
      notification.warning({
        message: "Audit Notes Required",
        description: "Please enter audit notes before requesting revision.",
      });
      return;
    }

    if (!passedId) {
      message.error("No request selected.");
      return;
    }

    setRequestingRevision(true);
    try {
      const res = await requestRevision(passedId, remarks);

      if (Math.floor(res?.status / 100) === 2) {
        notification.success({
          message: res?.data?.message || "Revision Requested",
        });
        navigate("/approvals-requests");
      } else {
        message.error(res?.data?.message || "Failed to request revision.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to request revision.",
      );
    } finally {
      setRequestingRevision(false);
    }
  };

  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);
  const fuelCardCount = Number(request.vehicleSlots) || 0;
  const kindInfo = request.kind ? REQUEST_KINDS[request.kind] : null;
  const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
  const newAddonModules = (request.addonPricing?.lines || []).filter(
    (line) => line.is_new_module,
  );

  // What approving does, per request type (confirm modal text).
  const approveText = (() => {
    if (request.kind === "renewal") {
      const highlight = plural(fuelCardCount, "vehicle");
      return {
        highlight,
        subtitle: `The vehicle limit for ${request.legalName} will be set to ${highlight} and the subscription renewed for one ${
          request.billingCycle === "annual" ? "year" : "month"
        }. Do you want to approve this renewal?`,
      };
    }
    if (request.kind === "addon") {
      const highlight = fuelCardCount
        ? plural(fuelCardCount, "extra vehicle slot")
        : plural(newAddonModules.length, "new module");
      const extras = [
        fuelCardCount ? plural(fuelCardCount, "extra vehicle slot") : null,
        newAddonModules.length
          ? plural(newAddonModules.length, "new module")
          : null,
      ].filter(Boolean);
      return {
        highlight,
        subtitle: `${extras.join(" and ")} will be added to ${request.legalName}'s subscription${
          fuelCardCount
            ? `, and ${plural(fuelCardCount, "fuel card")} generated`
            : ""
        }. The expiry date doesn't change. Do you want to approve this add-on?`,
      };
    }
    const highlight = `${fuelCardCount} fuel card${fuelCardCount === 1 ? "" : "s"}`;
    return {
      highlight,
      subtitle: `${fuelCardCount} Fuel Card${fuelCardCount === 1 ? "" : "s"} will be generated for ${request.legalName}. Do you want to approve this payment?`,
    };
  })();

  const vehiclesLabel =
    {
      initial: "Vehicles",
      renewal: "Total Vehicles",
      addon: "Extra Vehicle Slots",
    }[request.kind] || "Requested Vehicles";

  const addonColumns = [
    { title: "Module", dataIndex: "module_name", key: "module_name" },
    {
      title: "Type",
      dataIndex: "is_new_module",
      key: "is_new_module",
      render: (isNew) =>
        isNew ? <Tag color="purple">New module</Tag> : <Tag>Extra slots</Tag>,
    },
    { title: "Vehicles", dataIndex: "vehicles", key: "vehicles" },
    {
      title: "Full Price / Vehicle",
      dataIndex: "price_per_vehicle",
      key: "price_per_vehicle",
      render: (value) => formatCurrency(value),
    },
    {
      title: "Prorated Price / Vehicle",
      dataIndex: "prorated_price_per_vehicle",
      key: "prorated_price_per_vehicle",
      render: (value) => formatCurrency(value),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (value) => formatCurrency(value),
    },
  ];

  const handleApprove = () => {
    if (!remarks.trim()) {
      notification.warning({
        message: "Audit Notes Required",
        description: "Please enter audit notes before approving.",
      });
      return;
    }

    if (!passedId) {
      message.error("No request selected.");
      return;
    }

    if (request.requestType === "subscription_payment") {
      setIsApproveConfirmOpen(true);
      return;
    }

    submitApproval();
  };

  const submitApproval = async () => {
    setApproving(true);
    try {
      const res = await approveApproval(passedId, remarks);

      if (Math.floor(res?.status / 100) === 2) {
        notification.success({
          message: res?.data?.message || "Approved",
        });
        navigate("/approvals-requests");
      } else {
        message.error(res?.data?.message || "Failed to approve request.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to approve request.",
      );
    } finally {
      setApproving(false);
    }
  };

  const handleView = () => {
    if (!request.proofFile) {
      message.error("No proof of payment file uploaded.");
      return;
    }

    window.open(request.proofFile, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="subscription-payment-request">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="vendor-header-card">
        <div className="header-main-content">
          <div className="logo">
            {request.companyLogo && !logoFailed ? (
              <img
                src={request.companyLogo}
                alt={request.legalName}
                className="vendor-logo"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <InitialsAvatar
                name={request.legalName}
                size={64}
                shape="circle"
                style={{ fontSize: 22 }}
              />
            )}
          </div>
          <div className="vendor-details">
            <div className="company-row">
              <div className="company-name">{request.legalName}</div>
              <div className={`status-badge ${statusMap[currentStatus]}`}>
                {formatStatus(currentStatus)}
              </div>
              {kindInfo && (
                <Tag
                  className={`request-kind-tag request-kind-tag--${kindInfo.color}`}
                >
                  {kindInfo.label}
                </Tag>
              )}
            </div>
            <div className="info-row-header">
              <span>
                <img src={locIcon} alt="address icon" /> {request.address}
              </span>
              <span>
                <img src={regIcon} alt="reg icon" /> Reg:{" "}
                {request.registrationNumber}
              </span>
              <span>
                <img src={taxIcon} alt="email icon" /> {request.contactEmail}
              </span>
              <span>
                <img src={webIcon} alt="phone icon" /> {request.contactPhone}
              </span>
            </div>
          </div>
        </div>

        <div className="poc-card">
          <InitialsAvatar
            src={request.submittedByAvatar}
            name={request.submittedByName}
            alt="POC"
            className="poc-avatar"
            style={{ fontSize: 16 }}
          />
          <div className="poc-info">
            <span className="poc-label">Poc</span>
            <Tooltip title={request.submittedByName} placement="topLeft">
              <span className="poc-name">{request.submittedByName}</span>
            </Tooltip>

            <Tooltip title={request.submittedByEmail} placement="bottomLeft">
              <span className="poc-email">{request.submittedByEmail}</span>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="info-section">
        <div className="info-title">Attachment & Amount</div>
        <div className="attachment-grid">
          <div className="attachment-card">
            <div className="wrapper">
              <div className="file-info">
                <img
                  src={request.isWiPay ? creditIcon : docIcon}
                  alt={request.isWiPay ? "wipay" : "file"}
                  className="file-icon"
                />
                {request.isWiPay ? (
                  <div className="file-details">
                    <div className="file-name">Paid via WiPay</div>
                    <div className="wipay-meta">
                      <div className="wipay-meta-row">
                        <span className="wipay-meta-label">Status</span>
                        <span
                          className={`wipay-status wipay-status--${
                            request.wipayStatusKey || "unknown"
                          }`}
                        >
                          {request.wipayStatus}
                        </span>
                      </div>
                      <div className="wipay-meta-row">
                        <span className="wipay-meta-label">Payment Date</span>
                        <span className="wipay-meta-value">
                          {request.paymentDate}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="file-details">
                    <div className="file-name">
                      {request.paymentMethod !== "-"
                        ? `Proof of Payment (${formatStatus(request.paymentMethod)})`
                        : "Proof of Payment"}
                    </div>
                    <div className="file-date">
                      Payment Date: {request.paymentDate}
                    </div>
                  </div>
                )}
              </div>

              {!request.isWiPay && (
                <div className="file-actions">
                  <Button
                    icon={<EyeOutlined />}
                    type="text"
                    className="view-btn"
                    disabled={!request.proofFile}
                    onClick={() => handleView()}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="amount-card">
            <div className="amount-content">
              <div className="wrapper">
                <div className="amount-label">Amount</div>
                <div className="icon">
                  <img src={creditIcon} alt="credit" className="credit-icon" />
                </div>
              </div>
              <div className="amount-value">{request.amount}</div>
            </div>
          </div>
          {request.requestType === "subscription_payment" && (
            <div className="amount-card">
              <div className="amount-content">
                <div className="wrapper">
                  <div className="amount-label">{vehiclesLabel}</div>
                  <div className="icon">
                    <img
                      src={vehicleIcon}
                      alt="vehicles"
                      className="credit-icon"
                    />
                  </div>
                </div>
                <div className="amount-value">
                  {request.vehicleSlots ?? "-"}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {kindInfo && (
        <div className="info-section">
          <div className="info-title">Request Details</div>
          <div className="request-details-card">
            <div className="request-details-type">
              <Tag
                className={`request-kind-tag request-kind-tag--${kindInfo.color}`}
              >
                {kindInfo.label}
              </Tag>
              <span>{kindInfo.description}</span>
            </div>
            <div className="request-details-grid">
              <div>
                <div className="request-details-label">Billing Period</div>
                <div className="request-details-value">
                  {formatCycle(request.billingCycle)}
                </div>
              </div>
              <div>
                <div className="request-details-label">
                  {request.kind === "addon" ? "Modules Added" : "Modules"}
                </div>
                <div className="request-details-value">
                  {request.kind === "addon"
                    ? newAddonModules
                        .map((line) => line.module_name)
                        .join(", ") || "None (extra slots only)"
                    : request.modules.map((mod) => mod.name).join(", ") || "-"}
                </div>
              </div>
              <div>
                <div className="request-details-label">{vehiclesLabel}</div>
                <div className="request-details-value">
                  {request.vehicleSlots ?? "-"}
                </div>
              </div>
              <div>
                <div className="request-details-label">
                  Current Vehicle Limit
                </div>
                <div className="request-details-value">
                  {request.currentVehicleLimit ?? "-"}
                </div>
              </div>
              <div>
                <div className="request-details-label">Current Expiry Date</div>
                <div className="request-details-value">
                  {formatDate(request.expiresOn)}
                </div>
              </div>
              {request.kind === "addon" && request.addonPricing && (
                <div>
                  <div className="request-details-label">Prorated For</div>
                  <div className="request-details-value">
                    {request.addonPricing.remaining_days != null
                      ? `${plural(request.addonPricing.remaining_days, "day")} (until ${formatDate(
                          request.addonPricing.prorated_until,
                        )})`
                      : "Full cycle price"}
                  </div>
                </div>
              )}
            </div>
            {request.kind === "addon" &&
              (request.addonPricing?.lines || []).length > 0 && (
                <Table
                  className="request-details-table"
                  size="small"
                  pagination={false}
                  rowKey="module_id"
                  columns={addonColumns}
                  dataSource={request.addonPricing.lines}
                />
              )}
          </div>
        </div>
      )}

      {canEdit &&
        !request.isWiPay &&
        ["pending", "revision_requested"].includes(
          String(currentStatus).toLowerCase(),
        ) && (
          <div className="decision-panel">
            <div className="info-title">Decision Panel</div>
            <div className="decision-card">
              <div className="audit-section">
                <label>Audit Notes</label>
                <textarea
                  placeholder="Enter a description..."
                  className="audit-textarea"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
              <div className="decision-actions">
                <Button
                  className="reject-btn"
                  icon={<CloseCircleOutlined />}
                  onClick={handleReject}
                  loading={rejecting}
                  disabled={rejecting}
                >
                  Reject
                </Button>

                <Button
                  className="revision-btn"
                  icon={<ReloadOutlined />}
                  onClick={handleRevision}
                  loading={requestingRevision}
                  disabled={requestingRevision}
                >
                  Request Revision
                </Button>

                <Button
                  type="primary"
                  className="approve-btn"
                  icon={<CheckCircleOutlined />}
                  onClick={handleApprove}
                  loading={approving}
                  disabled={approving}
                >
                  Approve
                </Button>
              </div>
            </div>
          </div>
        )}
      <GenericModal
        open={isApproveConfirmOpen}
        title={`Approve ${kindInfo ? kindInfo.label : "Subscription Payment"}`}
        variant="success"
        subtitle={approveText.subtitle}
        highlightText={approveText.highlight}
        showReason={false}
        confirmText="Approve"
        cancelText="Cancel"
        iconColor="#16A34A"
        iconBgColor="#16A34A1a"
        confirmBtnColor="#16A34A"
        confirmBtnHoverColor="#15803D"
        onCancel={() => setIsApproveConfirmOpen(false)}
        onConfirm={() => {
          setIsApproveConfirmOpen(false);
          submitApproval();
        }}
      />
    </div>
  );
};

export default SubscriptionPaymentRequest;
